const ASSISTANT_ACTION_EVENT = "bpm:assistant-action";
const ACTION_SUCCESS_MESSAGE = "solicitação concluída.";
export const ASSISTANT_ACTION_TYPES = {
  OPEN_TAB: "open-tab",
  CLOSE_TAB: "close-tab",
  CLOSE_ALL_TABS: "close-all-tabs",
  RELOAD_PAGE: "reload-page",
};

const normalizeText = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildTokenSet = (keywords = []) => {
  const tokens = new Set();
  keywords.forEach((keyword) => {
    const normalized = normalizeText(keyword);
    if (!normalized) {
      return;
    }
    normalized.split(" ").forEach((token) => {
      if (token) {
        tokens.add(token);
      }
    });
  });
  return tokens;
};

const buildAssistantTabProps = (type, extras = {}) => ({
  assistantAction: { type, key: Date.now(), ...extras },
});

const EVENT_COMMAND_PATTERNS = [
  /\bnova?\s+(reuniao|reunião|chamada|call)\b/gi,
  /\bmarcar\s+(reuniao|reunião|chamada|call|evento|compromisso)\b/gi,
  /\bcriar\s+(evento|compromisso)\b/gi,
  /\bagendar\s+(evento|compromisso|reuniao|reunião|call)\b/gi,
  /\bnovo\s+(evento|compromisso)\b/gi,
  /\bna\s+agenda\b/gi,
  /\bpara\s+agenda\b/gi,
];

const clampNumber = (value, min, max) => {
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.min(Math.max(num, min), max);
};

const padTimePart = (value) => String(value).padStart(2, "0");

const extractTimeFromPrompt = (prompt = "") => {
  const timeMatch = prompt.match(/(\d{1,2})\s*(?::|h)\s*(\d{2})?/i);
  if (!timeMatch) {
    const hourOnly = prompt.match(/\b(?:as|às)\s*(\d{1,2})\b/i);
    if (!hourOnly) return { time: null, raw: null };
    const hour = clampNumber(hourOnly[1], 0, 23);
    if (hour == null) return { time: null, raw: null };
    return { time: `${padTimePart(hour)}:00`, raw: hourOnly[0] };
  }
  const hour = clampNumber(timeMatch[1], 0, 23);
  const minute = clampNumber(timeMatch[2] ?? "00", 0, 59);
  if (hour == null || minute == null) return { time: null, raw: null };
  return {
    time: `${padTimePart(hour)}:${padTimePart(minute)}`,
    raw: timeMatch[0],
  };
};

const offsetDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const extractRelativeDate = (normalizedPrompt) => {
  if (/\bhoje\b/.test(normalizedPrompt)) {
    return { date: formatDateInput(new Date()), raw: "hoje" };
  }
  if (/\bdepois\s+de\s+amanha\b/.test(normalizedPrompt)) {
    const target = offsetDate(2);
    return { date: formatDateInput(target), raw: "depois de amanha" };
  }
  if (/\bamanha\b/.test(normalizedPrompt)) {
    const target = offsetDate(1);
    return { date: formatDateInput(target), raw: "amanha" };
  }
  return { date: null, raw: null };
};

const extractExplicitDate = (prompt = "") => {
  const dateMatch = prompt.match(
    /(\d{1,2})\s*[-/]\s*(\d{1,2})(?:\s*[-/]\s*(\d{2,4}))?/,
  );
  if (!dateMatch) {
    return { date: null, raw: null };
  }
  const [, dayStr, monthStr, yearStr] = dateMatch;
  const day = clampNumber(dayStr, 1, 31);
  const month = clampNumber(monthStr, 1, 12);
  if (day == null || month == null) {
    return { date: null, raw: null };
  }
  const now = new Date();
  const normalizedYear = yearStr ? Number(yearStr) : now.getFullYear();
  const shortYear = yearStr ? clampNumber(yearStr, 0, 99) : null;
  const fullYear =
    yearStr && yearStr.length === 2 ? 2000 + (shortYear ?? 0) : normalizedYear;
  const candidate = new Date(fullYear, month - 1, day);
  if (Number.isNaN(candidate.getTime())) {
    return { date: null, raw: null };
  }
  if (!yearStr && candidate < now) {
    candidate.setFullYear(candidate.getFullYear() + 1);
  }
  return { date: formatDateInput(candidate), raw: dateMatch[0] };
};

const buildEventPrefillFromPrompt = (prompt = "") => {
  const normalizedPrompt = normalizeText(prompt);
  if (!normalizedPrompt) {
    return null;
  }

  const relativeDate = extractRelativeDate(normalizedPrompt);
  const explicitDate = extractExplicitDate(prompt);
  const { time, raw: rawTime } = extractTimeFromPrompt(prompt);
  const chosenDate = relativeDate.date || explicitDate.date || null;
  const rawDate = relativeDate.raw || explicitDate.raw || null;

  let title = prompt;
  EVENT_COMMAND_PATTERNS.forEach((pattern) => {
    title = title.replace(pattern, "");
  });
  [rawDate, rawTime].forEach((part) => {
    if (part) {
      title = title.replace(part, " ");
    }
  });
  title = title.replace(/\b(amanh[ãa]|hoje|depois de amanh[ãa])\b/gi, " ");
  title = title.replace(/\b(para|as|às|no dia|dia)\b/gi, " ");
  title = title.replace(
    /\b(com\s+nome\s+de|nome\s+de|com\s+o\s+nome\s+de|chamad[oa])\b/gi,
    " ",
  );
  title = title.replace(/\s+/g, " ").trim();

  const sanitizedTitle = title.replace(/^["'“”]+|["'“”]+$/g, "").trim();
  const genericTitleHint =
    /^(evento|compromisso|reuniao|reunião|call|chamada|nome)$/i.test(
      sanitizedTitle,
    );

  const prefill = {};
  if (sanitizedTitle && !genericTitleHint) {
    prefill.title =
      sanitizedTitle.charAt(0).toUpperCase() + sanitizedTitle.slice(1);
  }
  if (chosenDate) {
    prefill.date = chosenDate;
  }
  if (time) {
    prefill.time = time;
  }

  return Object.keys(prefill).length ? prefill : null;
};

const buildEventSuccessMessage = (prefill) => {
  if (!prefill?.title) {
    const clues = [];
    if (prefill?.date) {
      clues.push(`data ${prefill.date}`);
    }
    if (prefill?.time) {
      clues.push(`horário ${prefill.time}`);
    }
    const clueText = clues.length
      ? ` Já preenchi ${clues.join(" e ")} para adiantar.`
      : "";
    return `Abri a Agenda e deixei o formulário de novo evento pronto.${clueText} Qual título você quer usar nesse evento?`;
  }
  const highlights = [];
  if (prefill.title) {
    highlights.push(`título "${prefill.title}"`);
  }
  if (prefill.date) {
    highlights.push(`data ${prefill.date}`);
  }
  if (prefill.time) {
    highlights.push(`horário ${prefill.time}`);
  }
  if (!highlights.length) {
    return "Abri a Agenda e deixei o formulário de novo evento pronto. Qual título você quer usar nesse evento?";
  }
  const summary =
    highlights.length === 1
      ? highlights[0]
      : `${highlights.slice(0, -1).join(", ")} e ${highlights[highlights.length - 1]}`;
  return `Abri a Agenda, pré-preenchi ${summary} e deixei os demais campos prontos para conferência.`;
};

const SCREEN_SHORTCUTS = [
  {
    id: "open-dashboard",
    keywords: [
      "dashboard",
      "inicio",
      "painel",
      "home",
      "visao geral",
      "tela inicial",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "home",
      tabLabel: "Dashboard",
    },
    successMessage:
      "Levei você de volta ao Dashboard. Aproveite os cartões iniciais para visualizar pendências, indicadores e atalhos rápidos.",
  },
  {
    id: "open-mural",
    keywords: [
      "mural",
      "avisos",
      "comunicados",
      "noticias internas",
      "publicacoes",
      "feed interno",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "mural",
      tabLabel: "Mural",
    },
    successMessage:
      "Abri o Mural para você acompanhar comunicados, publicações e avisos internos.",
  },
  {
    id: "open-courses",
    keywords: [
      "cursos",
      "treinamentos",
      "capacitacao",
      "capacitação",
      "aprendizado",
      "universidade corporativa",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "courses",
      tabLabel: "Cursos",
    },
    successMessage:
      "Abri o módulo de Cursos para você acessar treinamentos, acompanhar progresso e concluir trilhas.",
  },
  {
    id: "open-requests",
    keywords: [
      "nova solicitacao",
      "abrir solicitacao",
      "abrir processo",
      "enviar processo",
      "minhas solicitacoes",
      "process runner",
      "executar processo",
      "agendar processo",
      "nova demanda",
      "acompanhar processo",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "requests",
      tabLabel: "Minhas solicitações",
    },
    successMessage:
      "Abri a aba Minhas solicitações. Clique em “Nova solicitação” para iniciar um processo ou utilize os filtros para assumir tarefas em andamento.",
  },
  {
    id: "open-forms",
    keywords: [
      "formularios",
      "novo formulario",
      "studio formularios",
      "form builder",
      "formulario inteligente",
      "editar formulario",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "forms",
      tabLabel: "Studio • Formulários",
    },
    successMessage:
      "Abri o Studio na aba Formulários para que você crie, edite e publique modelos. Use o botão “Novo formulário” no canto superior direito.",
  },
  {
    id: "open-processes",
    keywords: [
      "modelar processo",
      "process designer",
      "desenhar bpmn",
      "studio processos",
      "novo fluxo",
      "diagramar",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "processes",
      tabLabel: "Studio • Processos",
    },
    successMessage:
      "Abri o Studio na aba Processos para você desenhar o fluxo em BPMN, configurar gateways e publicar novas versões.",
  },
  {
    id: "open-automation",
    keywords: ["automacoes", "rpa", "hub de automacao", "robos", "automatizar"],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "automation-hub",
      tabLabel: "Automações e IA",
    },
    successMessage:
      "Abri o hub de Automações e IA para você acompanhar integrações, robôs e orquestrações disponíveis.",
  },
  {
    id: "open-agenda",
    keywords: [
      "agenda",
      "calendario",
      "compromissos",
      "lembretes",
      "agendamentos",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "agenda",
      tabLabel: "Agenda",
    },
    successMessage:
      "Abri a Agenda colaborativa. Utilize os filtros por equipe e o botão “Novo compromisso” para registrar reuniões ou SLAs.",
  },
  {
    id: "open-manual",
    keywords: [
      "manual",
      "documentacao",
      "ajuda",
      "tutorial",
      "guia do usuario",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "user-manual",
      tabLabel: "Manual do usuário",
    },
    successMessage:
      "Abri o Manual do usuário na íntegra. Utilize a busca lateral para localizar capítulos e fluxos ilustrados.",
  },
  {
    id: "open-crm",
    keywords: ["crm", "pipeline", "negocios", "oportunidades", "vendas"],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "crm",
      tabLabel: "CRM",
    },
    successMessage:
      "Abri o módulo de CRM para acompanhar oportunidades, contatos e funil de vendas.",
  },
  {
    id: "open-finance",
    keywords: [
      "financeiro",
      "pagamentos",
      "contas a pagar",
      "contas a receber",
      "tesouraria",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "finance",
      tabLabel: "Financeiro",
    },
    successMessage:
      "Abri o módulo Financeiro. Consulte lançamentos, centros de custo e gere novos relatórios diretamente dessa aba.",
  },
  {
    id: "open-student-management",
    keywords: [
      "gestao de alunos",
      "gestão de alunos",
      "alunos",
      "cadastro de aluno",
      "frequencia de alunos",
      "histórico de frequencia",
      "historico de frequencia",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "student-management",
      tabLabel: "Gestão de Alunos",
    },
    successMessage:
      "Abri Gestão de Alunos. Você pode consultar alunos, turmas, cursos, frequência, histórico e configurações.",
  },
  {
    id: "open-attendee-management",
    keywords: [
      "gestao de atendidos",
      "gestão de atendidos",
      "atendidos",
      "cadastro de atendido",
      "frequencia de atendidos",
      "histórico de frequencia",
      "historico de frequencia",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "attendee-management",
      tabLabel: "Gestão de Atendidos",
    },
    successMessage:
      "Abri Gestão de Atendidos. Você pode consultar atendidos, turmas, grupos, frequência, histórico e configurações.",
  },
  {
    id: "open-project-bank",
    keywords: [
      "banco de projetos",
      "projetos",
      "novo projeto",
      "criar projeto",
      "fazer projeto",
      "projeto social",
      "projeto institucional",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "project-bank",
      tabLabel: "Banco de Projetos",
    },
    successMessage:
      "Abri o Banco de Projetos. Clique em Novo Projeto; posso resumir seu prompt em título, objetivo, justificativa e público-alvo.",
  },
  {
    id: "open-inventory",
    keywords: ["estoque", "inventario", "almoxarifado", "itens", "materiais"],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "inventory",
      tabLabel: "Estoque",
    },
    successMessage:
      "Abri o módulo de Estoque para você acompanhar movimentações, saldo e requisições.",
  },
  {
    id: "open-profile",
    keywords: [
      "perfil",
      "dados pessoais",
      "senha",
      "atualizar usuario",
      "foto de perfil",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "profile",
      tabLabel: "Perfil",
    },
    successMessage:
      "Abri sua tela de Perfil. Ajuste foto, assinatura e redefina a senha sempre que necessário.",
  },
  {
    id: "open-file-manager",
    keywords: [
      "arquivos",
      "documentos",
      "central de arquivos",
      "upload",
      "download",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "file-manager",
      tabLabel: "Arquivos",
    },
    successMessage:
      "Abri o gerenciador de Arquivos. Organize pastas, envie documentos e compartilhe links com a equipe.",
  },
  {
    id: "open-my-team",
    keywords: [
      "minha equipe",
      "minha area",
      "meu time",
      "subordinados",
      "time direto",
      "aba da equipe",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "my-team",
      tabLabel: "Minha equipe",
    },
    successMessage:
      "Abri a aba Minha equipe para você acompanhar as pendências dos subordinados e assumir tarefas quando necessário.",
  },
  {
    id: "open-asset-management",
    keywords: [
      "patrimonio",
      "controle de patrimonio",
      "gestao de ativos",
      "ativos fixos",
      "patrimonial",
      "patrimonio corporativo",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "asset-management",
      tabLabel: "Patrimônio",
    },
    successMessage:
      "Abri o módulo de Patrimônio para você acompanhar bens, inventários e etiquetas de controle.",
  },
  {
    id: "open-registration-center",
    keywords: [
      "central de cadastros",
      "cadastros",
      "cadastro corporativo",
      "central cadastro",
      "base de cadastros",
    ],
    action: {
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "registration-center",
      tabLabel: "Central de Cadastros",
    },
    successMessage:
      "Abri a Central de Cadastros para você consultar e manter as fichas disponíveis.",
  },
  {
    id: "create-event",
    keywords: [
      "criar evento",
      "novo evento",
      "agendar evento",
      "agendar reuniao",
      "agendar compromisso",
      "criar compromisso",
      "marcar evento",
      "marcar compromisso",
      "marcar reuniao",
      "marcar chamada",
      "marcar call",
      "cadastrar evento",
    ],
    resolveAction: (prompt) => {
      const prefill = buildEventPrefillFromPrompt(prompt);
      return {
        type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
        tabId: "agenda",
        tabLabel: "Agenda",
        tabProps: buildAssistantTabProps(
          "new-event",
          prefill ? { prefill } : {},
        ),
        successMessage: buildEventSuccessMessage(prefill),
      };
    },
  },
  {
    id: "create-form",
    keywords: [
      "criar formulario",
      "novo formulario",
      "form builder",
      "construir formulario",
      "cadastrar formulario",
      "desenhar formulario",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "forms",
      tabLabel: "Studio • Formulários",
      tabProps: buildAssistantTabProps("new-form"),
    }),
    successMessage:
      "Abri o Studio na aba Formulários e carreguei o construtor para você iniciar um modelo em branco.",
  },
  {
    id: "create-process",
    keywords: [
      "criar processo",
      "novo processo",
      "modelar processo",
      "diagramar processo",
      "criar fluxo",
      "desenhar bpmn do zero",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
      tabId: "processes",
      tabLabel: "Studio • Processos",
      tabProps: buildAssistantTabProps("new-process"),
    }),
    successMessage:
      "Abri o Studio em Processos e iniciei o modelador para você publicar um novo fluxo.",
  },
  {
    id: "close-mural",
    keywords: [
      "fechar mural",
      "feche mural",
      "encerrar mural",
      "sair do mural",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.CLOSE_TAB,
      tabId: "mural",
    }),
    successMessage:
      "Fechei a aba do Mural. Se quiser, eu reabro quando você pedir.",
  },
  {
    id: "close-courses",
    keywords: [
      "fechar cursos",
      "feche cursos",
      "encerrar cursos",
      "sair de cursos",
      "fechar treinamento",
      "fechar treinamentos",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.CLOSE_TAB,
      tabId: "courses",
    }),
    successMessage:
      "Fechei a aba de Cursos. Posso abrir novamente quando quiser continuar os estudos.",
  },
  {
    id: "close-all-tabs",
    keywords: [
      "fechar abas",
      "feche abas",
      "apagar abas",
      "limpar abas",
      "limpar",
      "limpa",
      "fechar todas as abas",
      "encerrar abas",
      "sair das abas",
      "finalizar abas",
      "terminar abas",
      "concluir abas",
      "fechar tudo",
      "encerrar tudo",
      "finalizar tudo",
      "terminar tudo",
      "concluir tudo",
      "encerrar geral",
      "finalizar geral",
      "terminar geral",
      "concluir geral",
      "abas",
      "todas as abas",
      "tudo",
      "geral",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.CLOSE_ALL_TABS,
    }),
    successMessage:
      "Fechei todas as abas abertas e mantive apenas o Dashboard ativo.",
  },
  {
    id: "reload-page",
    keywords: [
      "recarregar pagina",
      "recarrega a pagina",
      "atualizar pagina",
      "atualizar abas",
      "atualize abas",
      "dar refresh",
      "atualiza a tela",
      "recarrega tudo",
    ],
    resolveAction: () => ({
      type: ASSISTANT_ACTION_TYPES.RELOAD_PAGE,
    }),
    successMessage:
      "Recarregando a interface para garantir que tudo fique sincronizado.",
  },
];

const ENRICHED_SHORTCUTS = SCREEN_SHORTCUTS.map((shortcut) => ({
  ...shortcut,
  tokenSet: buildTokenSet(shortcut.keywords),
}));

const CLOSE_INTENT_KEYWORDS = [
  "fechar",
  "feche",
  "encerrar",
  "encerre",
  "sair",
  "ocultar",
  "finalizar",
  "finalize",
  "terminar",
  "termine",
  "concluir",
  "conclua",
];

const CLOSE_ALL_HINTS = ["todas as abas", "todas abas", "tudo", "geral"];

const CLOSEABLE_TAB_SHORTCUTS = ENRICHED_SHORTCUTS.filter((shortcut) => {
  const action = shortcut?.action;
  return (
    action?.type === ASSISTANT_ACTION_TYPES.OPEN_TAB &&
    action?.tabId &&
    action.tabId !== "home"
  );
});

const resolveCloseTabIntent = (normalizedPrompt = "", promptTokens = []) => {
  if (!normalizedPrompt) {
    return null;
  }
  const hasCloseIntent = CLOSE_INTENT_KEYWORDS.some((keyword) =>
    normalizedPrompt.includes(keyword),
  );
  if (!hasCloseIntent) {
    return null;
  }

  const ranked = CLOSEABLE_TAB_SHORTCUTS.map((shortcut) => {
    let score = 0;
    shortcut.keywords.forEach((keyword) => {
      if (normalizedPrompt.includes(normalizeText(keyword))) {
        score += Math.max(2, keyword.split(" ").length);
      }
    });
    promptTokens.forEach((token) => {
      if (shortcut.tokenSet.has(token)) {
        score += 1;
      }
    });
    return { shortcut, score };
  })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const bestMatch = ranked[0];
  if (bestMatch && bestMatch.score >= 2) {
    const tabId = bestMatch.shortcut.action?.tabId;
    const tabLabel =
      bestMatch.shortcut.action?.tabLabel ||
      bestMatch.shortcut.successMessage ||
      "aba solicitada";
    if (!tabId) {
      return null;
    }
    return {
      id: `close-${tabId}`,
      type: ASSISTANT_ACTION_TYPES.CLOSE_TAB,
      tabId,
      successMessage: `Fechei a aba ${tabLabel}. Se quiser, posso reabrir em seguida.`,
    };
  }

  const isCloseAllIntent =
    CLOSE_ALL_HINTS.some((hint) => normalizedPrompt.includes(hint)) ||
    /\babas?\b/.test(normalizedPrompt);
  if (isCloseAllIntent) {
    return null;
  }
  return null;
};

export const resolveAssistantAction = (prompt) => {
  const normalizedPrompt = normalizeText(prompt);
  if (!normalizedPrompt) {
    return null;
  }
  const promptTokens = normalizedPrompt.split(" ").filter(Boolean);
  const closeTabIntent = resolveCloseTabIntent(normalizedPrompt, promptTokens);
  if (closeTabIntent) {
    return closeTabIntent;
  }
  const ranked = ENRICHED_SHORTCUTS.map((shortcut) => {
    let score = 0;
    shortcut.keywords.forEach((keyword) => {
      if (normalizedPrompt.includes(normalizeText(keyword))) {
        score += Math.max(2, keyword.split(" ").length);
      }
    });
    promptTokens.forEach((token) => {
      if (shortcut.tokenSet.has(token)) {
        score += 1;
      }
    });
    return { shortcut, score };
  })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const bestMatch = ranked[0];
  if (!bestMatch || bestMatch.score < 2) {
    return null;
  }

  const { shortcut } = bestMatch;
  const resolvedAction =
    typeof shortcut.resolveAction === "function"
      ? shortcut.resolveAction(prompt)
      : shortcut.action;
  if (!resolvedAction) {
    return null;
  }
  const successMessage =
    resolvedAction.successMessage ||
    shortcut.successMessage ||
    ACTION_SUCCESS_MESSAGE;
  const { successMessage: _ignored, ...actionWithoutMessage } = resolvedAction;
  return {
    id: shortcut.id,
    ...actionWithoutMessage,
    successMessage,
  };
};

export const triggerAssistantAction = (action) => {
  if (!action || typeof window === "undefined") {
    return false;
  }
  window.dispatchEvent(
    new CustomEvent(ASSISTANT_ACTION_EVENT, {
      detail: action,
    }),
  );
  return true;
};

export { ASSISTANT_ACTION_EVENT };
